import {
  isGreQuantAnswerCorrect,
  isGreQuantQuestionAnswered,
} from './quantAnswerUtils';

export const SESSION_HISTORY_KEY = 'liwaza_quant_practice_history';
export const SEEN_QUESTIONS_KEY = 'liwaza_quant_seen_questions';

const getSessionHistoryKey = (userEmail) =>
  `${SESSION_HISTORY_KEY}_${userEmail}`;

const getSeenQuestionsKey = (userEmail) =>
  `${SEEN_QUESTIONS_KEY}_${userEmail}`;

export const loadSessionHistory = (userEmail) => {
  try {
    const data = localStorage.getItem(getSessionHistoryKey(userEmail));
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error loading session history:', error);
    return [];
  }
};

export const saveSessionHistory = (userEmail, history) => {
  try {
    localStorage.setItem(
      getSessionHistoryKey(userEmail),
      JSON.stringify(history.slice(0, 50))
    );
  } catch (error) {
    console.error('Error saving session history:', error);
  }
};

export const upsertSessionInHistory = (userEmail, sessionData) => {
  const history = loadSessionHistory(userEmail);
  const existingIndex = history.findIndex(
    (session) => session.id === sessionData.id
  );

  if (existingIndex >= 0) {
    history[existingIndex] = sessionData;
  } else {
    history.unshift(sessionData);
  }

  saveSessionHistory(userEmail, history);
  return history;
};

export const loadLocalSeenQuestionIds = (userEmail) => {
  try {
    const data = localStorage.getItem(getSeenQuestionsKey(userEmail));
    const parsed = data ? JSON.parse(data) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (error) {
    console.error('Error loading seen question IDs:', error);
    return new Set();
  }
};

export const saveLocalSeenQuestionIds = (userEmail, questionIds) => {
  try {
    localStorage.setItem(
      getSeenQuestionsKey(userEmail),
      JSON.stringify([...questionIds])
    );
  } catch (error) {
    console.error('Error saving seen question IDs:', error);
  }
};

export const mergeLocalSeenQuestionIds = (userEmail, questionIds = []) => {
  const merged = loadLocalSeenQuestionIds(userEmail);
  questionIds.forEach((id) => {
    if (id) merged.add(id);
  });
  saveLocalSeenQuestionIds(userEmail, merged);
  return merged;
};

export const getSessionAnswer = (answers, index) =>
  answers?.[index] ?? answers?.[String(index)];

export const extractAnsweredQuestionIds = (questions = [], answers = {}) => {
  const ids = [];
  questions.forEach((question, index) => {
    if (!question?.id) return;
    const userAnswer = getSessionAnswer(answers, index);
    if (isGreQuantQuestionAnswered(question, userAnswer)) {
      ids.push(question.id);
    }
  });
  return ids;
};

export const getAnsweredQuestionIdsFromSession = (session) =>
  extractAnsweredQuestionIds(session?.questions, session?.answers);

export const getSeenQuestionIdsFromHistory = (history = []) => {
  const seen = new Set();
  history.forEach((session) => {
    getAnsweredQuestionIdsFromSession(session).forEach((id) => seen.add(id));
  });
  return seen;
};

export const getCombinedSeenQuestionIds = (
  userEmail,
  history = [],
  backendSeenIds = []
) => {
  const combined = loadLocalSeenQuestionIds(userEmail);
  getSeenQuestionIdsFromHistory(history).forEach((id) => combined.add(id));
  backendSeenIds.forEach((id) => {
    if (id) combined.add(id);
  });
  return combined;
};

export const getMistakeQuestions = (history = []) => {
  const mistakesById = new Map();

  history.forEach((session) => {
    if (!session?.questions || !session?.answers) return;

    session.questions.forEach((question, index) => {
      const userAnswer = getSessionAnswer(session.answers, index);
      if (!isGreQuantQuestionAnswered(question, userAnswer)) return;
      if (isGreQuantAnswerCorrect(question, userAnswer)) return;
      if (!question?.id) return;

      mistakesById.set(question.id, {
        ...question,
        sessionId: session.id,
        sessionDate: session.timestamp || session.completedAt,
      });
    });
  });

  return Array.from(mistakesById.values());
};

export const mergeSessionHistory = (localHistory = [], backendHistory = []) => {
  const sessionMap = new Map();

  localHistory.forEach((session) => {
    if (session?.id) {
      sessionMap.set(session.id, session);
    }
  });

  backendHistory.forEach((session) => {
    const id = session?.id || session?.session_id;
    if (!id) return;

    const local = sessionMap.get(id);
    sessionMap.set(id, {
      id,
      timestamp: session.timestamp || local?.timestamp,
      config: session.config ?? local?.config,
      questions: session.questions ?? local?.questions,
      answers: session.answers ?? local?.answers,
      questionTimes:
        session.question_times ||
        session.questionTimes ||
        local?.questionTimes,
      results: session.results ?? local?.results,
      flaggedQuestions:
        session.flagged_questions ||
        session.flaggedQuestions ||
        local?.flaggedQuestions,
      stimuli: session.stimuli ?? local?.stimuli,
    });
  });

  const merged = Array.from(sessionMap.values());
  merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return merged.slice(0, 50);
};

export const clearLocalQuantPracticeData = (userEmail) => {
  if (!userEmail) return;
  localStorage.removeItem(getSessionHistoryKey(userEmail));
  localStorage.removeItem(getSeenQuestionsKey(userEmail));
};
